import { Trans, useLingui } from '@lingui/react/macro';
import { Typography } from '@remarkable/ark-web';
import { useId } from 'react';

import type { useEnterpriseEnrollmentErrorApiInit } from 'src/api/queries/enterpriseEnrollmentErrors';
import { NotificationBox } from 'src/components';

type InitError = ReturnType<
  typeof useEnterpriseEnrollmentErrorApiInit
>['error'];

interface Props {
  error: InitError;
}

export const EnterpriseEnrollmentInitErrorNotification = ({ error }: Props) => {
  const { t } = useLingui();
  const id = useId();

  const titleId = `${id}-title`;

  if (!error) return null;

  const type = error?.type || 'unknown';

  switch (type) {
    case 'https://errors.cloud.remarkable.com/enterprise/domain-connected':
      return (
        <div role="alert" aria-labelledby={titleId}>
          <NotificationBox
            variant="error"
            title={<span id={titleId}>{t`Domain already connected`}</span>}
          >
            {/* Add gap between title, list, and support "footer":  */}
            <div className="flex flex-col gap-8">
              <Typography variant="body-sm" as="p">
                <Trans>
                  This email domain is already used in an enrollment started by
                  another user.
                </Trans>
              </Typography>
              <ul className="flex list-disc flex-col gap-8 pl-24">
                <li>
                  <Typography variant="body-sm" as="p">
                    <Trans>
                      If you can access the user account that started the
                      enrollment, either continue or cancel that enrollment. If
                      cancelled, this enrollment can be retried and continued.
                    </Trans>
                  </Typography>
                </li>
                <li>
                  <Typography variant="body-sm" as="p">
                    <Trans>
                      If the user account that started the enrollment has been
                      deleted, that enrollment will expire after 24 hours. You
                      can retry this enrollment once it has expired.
                    </Trans>
                  </Typography>
                </li>
                <li>
                  <Typography variant="body-sm" as="p">
                    <Trans>
                      If the other enrollment is completed, that subscription
                      will need to be offboarded for the domain to be used
                      again.
                    </Trans>
                  </Typography>
                </li>
              </ul>
              <Typography variant="body-sm" as="p">
                <Trans>If you need help, please contact support.</Trans>
              </Typography>
            </div>
          </NotificationBox>
        </div>
      );
    default:
      return (
        <div role="alert" aria-labelledby={titleId}>
          <NotificationBox
            variant="error"
            title={<span id={titleId}>{t`Something went wrong`}</span>}
          >
            <Typography variant="body-sm" as="p">
              <Trans>
                Try again later or contact support if the problem persists.
              </Trans>
            </Typography>
          </NotificationBox>
        </div>
      );
  }
};
